import { useEffect, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { sendMessage, getMessagingDevices } from '../../services/api/messageApi';
import { updateDeviceAttribute } from '../../services/api/deviceApi';
import { RED_FLAG_MESSAGE, RED_FLAG_CANCEL_MESSAGE } from '../../config/redFlagConfig';
import { setDeviceStatus } from '../../services/api/traccarManagerApi';
import './RedFlagModal.css';

const RedFlagModal = ({ isOpen, onClose, stages = [], devices = [], initialStageName = '' }) => {
    const [activeTab, setActiveTab] = useState('stage'); // 'stage' | 'all' | 'redflag_cars'
    const [selectedStageName, setSelectedStageName] = useState('');
    const [selectedDeviceIds, setSelectedDeviceIds] = useState(new Set());
    const [sendingStatus, setSendingStatus] = useState({}); // { deviceId: 'pending' | 'sending' | 'sent' | 'error' }
    const [nceDevices, setNceDevices] = useState([]);

    // Set stage every time the modal opens — prefer the stage selected on the main map
    useEffect(() => {
        if (isOpen) {
            setSelectedStageName(initialStageName || (stages.length > 0 ? stages[0].name : ''));
        }
    }, [isOpen]);

    // Fetch 1NCE devices for matching
    useEffect(() => {
        if (isOpen) {
            getMessagingDevices()
                .then(data => {
                    console.log('Fetched 1NCE devices:', data);
                    setNceDevices(data || []);
                })
                .catch(err => console.error('Failed to fetch 1NCE devices', err));
        }
    }, [isOpen]);

    // Only show cars with no model set or model "Kierowca"
    const isEligible = (d) => {
        const model = (d.deviceModel || d.model || '').trim().toLowerCase();
        return model === '' || model === 'kierowca';
    };

    // Compute devices based on active tab
    const displayedDevices = useMemo(() => {
        if (activeTab === 'stage') {
            if (!selectedStageName) return [];
            return devices
                .filter(d => isEligible(d) && d.onStage && d.routeName === selectedStageName)
                .sort((a, b) => {
                    const distA = parseFloat(a.distanceOnPolyline) || 0;
                    const distB = parseFloat(b.distanceOnPolyline) || 0;
                    return distA - distB;
                });
        } else if (activeTab === 'redflag_cars') {
            return devices.filter(d => {
                const attrs = d.attributes || {};
                return isEligible(d) && (attrs.CarStatus === 'REDFLAG' || attrs.CarStatus === 'REDFLAG_SEND' || attrs.CarStatus === 'REDFLAG_CONFIRM' || attrs.redflag === true);
            }).sort((a, b) => {
                const numA = parseInt((a.deviceName || '0').split('-')[0].trim()) || 0;
                const numB = parseInt((b.deviceName || '0').split('-')[0].trim()) || 0;
                return numA - numB;
            });
        } else {
            return devices.filter(isEligible).sort((a, b) => {
                const numA = parseInt((a.deviceName || '0').split('-')[0].trim()) || 0;
                const numB = parseInt((b.deviceName || '0').split('-')[0].trim()) || 0;
                return numA - numB;
            });
        }
    }, [devices, selectedStageName, activeTab]);

    // reset selection when tab or stage changes changes? 
    // Maybe better to NOT auto-select all, or user request implies select behavior?
    // "select selected car" implies manual selection.
    // Existing logic was "Auto-select all". Let's keep auto-select for STAGE tab, but maybe manual for ALL?
    // User said "show all cars on list sort by number".
    // I will keep selection clearing on tab switch to avoid confusion, 
    // BUT for redflag_cars we might want to default select all for efficiency?
    // Let's stick to consistent manual/auto behavior.
    useEffect(() => {
        if (activeTab === 'stage' || activeTab === 'redflag_cars') {
            const ids = new Set(displayedDevices.map(d => d.deviceId || d.id));
            setSelectedDeviceIds(ids);
        } else {
            setSelectedDeviceIds(new Set()); // Start empty for 'all' to avoid massive accidental send
        }
        setSendingStatus({});
    }, [activeTab, selectedStageName, displayedDevices.length]);

    if (!isOpen) return null;

    const toggleDevice = (id) => {
        const next = new Set(selectedDeviceIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedDeviceIds(next);
    };

    const toggleAll = () => {
        const allIds = displayedDevices.map(d => d.deviceId || d.id);
        const allSelected = allIds.every(id => selectedDeviceIds.has(id));

        if (allSelected) {
            setSelectedDeviceIds(new Set());
        } else {
            setSelectedDeviceIds(new Set(allIds));
        }
    };

    const handleSendRedFlag = async () => {
        if (selectedDeviceIds.size === 0) {
            alert('No vehicles selected.');
            return;
        }

        if (!window.confirm(`Are you sure you want to send RED FLAG SMS to ${selectedDeviceIds.size} vehicles ? This is an emergency action.`)) {
            return;
        }

        const idsToProcess = Array.from(selectedDeviceIds);

        // Mark as sending
        const newStatus = { ...sendingStatus };
        idsToProcess.forEach(id => { newStatus[id] = 'sending'; });
        setSendingStatus(newStatus);

        await Promise.all(idsToProcess.map(async (deviceId) => {
            try {
                const traccarDevice = devices.find(d => (d.deviceId || d.id) === deviceId);
                if (!traccarDevice) throw new Error('Traccar device not found');

                const traccarId = String(traccarDevice.deviceId || traccarDevice.id).trim();

                // Match by DeviceIDTraccar — works for both Twilio (model=1) and 1NCE (model=2)
                const msgDevice = nceDevices.find(n =>
                    String(n.DeviceIDTraccar || '').trim() === traccarId
                );

                if (!msgDevice) {
                    throw new Error(`No messaging device found for Traccar ID ${traccarId}`);
                }

                const isTwilio = String(msgDevice.model) === '1';
                if (isTwilio && !msgDevice.phone) {
                    throw new Error(`Device ${traccarId} (Twilio) has no phone number`);
                }
                if (!isTwilio && !msgDevice.iccid) {
                    throw new Error(`Device ${traccarId} (1NCE) has no ICCID`);
                }

                // msgDevice has model, DeviceIDTraccar, phone, iccid — sendMessage routes correctly
                await sendMessage(msgDevice, RED_FLAG_MESSAGE);
                setSendingStatus(prev => ({ ...prev, [deviceId]: 'sent' }));

                // --- NEW: Update Device Attribute immediately ---
                try {
                    await updateDeviceAttribute(traccarId, 'CarStatus', 'REDFLAG_SEND');
                } catch (attrError) {
                    console.error(`Failed to update device attribute for ${traccarId}`, attrError);
                    // We don't throw here to avoid failing the SMS sending flow if just attribute fails
                }

            } catch (err) {
                console.error(`Failed to send to ${deviceId} `, err);
                setSendingStatus(prev => ({ ...prev, [deviceId]: 'error' }));
            }
        }));

    };

    const handleRemoveRedFlag = async () => {
        if (selectedDeviceIds.size === 0) {
            alert('No vehicles selected.');
            return;
        }

        if (!window.confirm(`Are you sure you want to REMOVE RED FLAG from ${selectedDeviceIds.size} vehicles? Cancellation SMS will be sent.`)) {
            return;
        }

        const idsToProcess = Array.from(selectedDeviceIds);

        const newStatus = { ...sendingStatus };
        idsToProcess.forEach(id => { newStatus[id] = 'sending'; });
        setSendingStatus(newStatus);

        await Promise.all(idsToProcess.map(async (deviceId) => {
            try {
                const traccarDevice = devices.find(d => (d.deviceId || d.id) === deviceId);
                const traccarId = String(deviceId).trim();

                const msgDevice = nceDevices.find(n =>
                    String(n.DeviceIDTraccar || '').trim() === traccarId
                );

                if (msgDevice) {
                    try {
                        await sendMessage(msgDevice, RED_FLAG_CANCEL_MESSAGE);
                    } catch (smsErr) {
                        console.error(`SMS Cancel failed for ${traccarId}`, smsErr);
                    }
                }

                await setDeviceStatus(deviceId, 'CLEAR');
                setSendingStatus(prev => ({ ...prev, [deviceId]: 'sent' }));
            } catch (err) {
                console.error(`Failed to clear for ${deviceId}`, err);
                setSendingStatus(prev => ({ ...prev, [deviceId]: 'error' }));
            }
        }));
    };

    const allSelected = displayedDevices.length > 0 && displayedDevices.every(d => selectedDeviceIds.has(d.deviceId || d.id));

    return ReactDOM.createPortal(
        <div className="red-flag-modal-overlay">
            <div className="red-flag-modal-container glass-effect">
                {/* Header */}
                <div className="px-8 pt-8 pb-0 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <div className="flex items-center gap-3 text-emergency-red mb-1">
                                <span className="material-symbols-outlined font-bold text-2xl">report</span>
                                <h2 className="text-2xl font-bold leading-tight tracking-[-0.015em] text-emergency-red">
                                    Emergency Signal: RED FLAG
                                </h2>
                            </div>
                            <p className="text-gray-custom text-sm">Deploying a red flag for all selected vehicles immediately.</p>
                        </div>
                        <button onClick={onClose} className="text-gray-custom hover:text-black transition-colors">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-8">
                        <button
                            onClick={() => setActiveTab('stage')}
                            className={`pb-3 text-sm font-bold border-b-2 flex items-center gap-2 transition-colors ${activeTab === 'stage'
                                ? 'border-primary-blue text-primary-blue'
                                : 'border-transparent text-gray-500 hover:text-black'
                                } `}
                        >
                            <span className="material-symbols-outlined text-lg">route</span>
                            CURRENT STAGE
                        </button>
                        <button
                            onClick={() => setActiveTab('all')}
                            className={`pb-3 text-sm font-bold border-b-2 flex items-center gap-2 transition-colors ${activeTab === 'all'
                                ? 'border-primary-blue text-primary-blue'
                                : 'border-transparent text-gray-500 hover:text-black'
                                } `}
                        >
                            <span className="material-symbols-outlined text-lg">group</span>
                            ALL CARS
                        </button>
                        <button
                            onClick={() => setActiveTab('redflag_cars')}
                            className={`pb-3 text-sm font-bold border-b-2 flex items-center gap-2 transition-colors ${activeTab === 'redflag_cars'
                                ? 'border-primary-blue text-primary-blue'
                                : 'border-transparent text-gray-500 hover:text-black'
                                } `}
                        >
                            <span className="material-symbols-outlined text-lg">flag</span>
                            REDFLAG CARS
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="modal-scroll-area">
                    <div className="px-8 py-6">
                        {/* Stage Selector - Only visible in 'stage' tab */}
                        {activeTab === 'stage' && (
                            <div className="flex max-w-[480px] flex-wrap items-end gap-4 mb-6">
                                <label className="flex flex-col min-w-40 flex-1">
                                    <p className="text-black dark:text-white text-xs font-bold uppercase tracking-wider pb-2">Active Special Stage (OS)</p>
                                    <div className="relative">
                                        <select
                                            className="appearance-none flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-black focus:outline-0 focus:ring-2 focus:ring-blue-500 border border-gray-300 bg-white h-10 px-3 text-sm font-medium leading-normal"
                                            value={selectedStageName}
                                            onChange={(e) => setSelectedStageName(e.target.value)}
                                        >
                                            {stages.map(s => (
                                                <option key={s.name} value={s.name}>
                                                    {s.name} {s.routeIndex?.cum ? `(${(s.routeIndex.cum[s.routeIndex.cum.length - 1] / 1000).toFixed(1)} km)` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                                            <span className="material-symbols-outlined">expand_more</span>
                                        </div>
                                    </div>
                                </label>
                            </div>
                        )}

                        {/* Table */}
                        <div className="red-flag-table-container">
                            <table className="w-full red-flag-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '60px' }}>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox-custom"
                                                    checked={allSelected}
                                                    onChange={toggleAll}
                                                />
                                                <label className="text-[10px] uppercase tracking-wider cursor-pointer font-bold text-black" onClick={toggleAll}>Select All</label>
                                            </div>
                                        </th>
                                        <th>Car #</th>
                                        <th>Driver / Co-driver</th>
                                        <th>Current KM</th>
                                        <th>Current Speed</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedDevices.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center py-8 text-gray-500">
                                                {activeTab === 'stage'
                                                    ? 'No active cars found on this stage.'
                                                    : activeTab === 'redflag_cars'
                                                        ? 'No cars with active RED FLAG found.'
                                                        : 'No cars found.'}
                                            </td>
                                        </tr>
                                    ) : (
                                        displayedDevices.map(device => {
                                            const id = device.deviceId || device.id;
                                            const isSelected = selectedDeviceIds.has(id);
                                            const status = sendingStatus[id];
                                            const carNum = (device.deviceName || 'Unknown').split('-')[0].trim();
                                            // Format: "101 - Driver/CoDriver". Remove num, then remove " - " separator.
                                            const teamName = (device.deviceName || '').replace(carNum, '').replace(/^\s*-\s*/, '').trim();

                                            // Safe access to distance, might not exist if not on stage or in 'all' view sometimes
                                            const distance = device.distanceOnPolyline
                                                ? `${parseFloat(device.distanceOnPolyline).toFixed(1)} km`
                                                : '-';

                                            return (
                                                <tr key={id} className={`hover:bg-blue-50 transition-colors ${isSelected ? 'bg-blue-50/30' : ''}`}>
                                                    <td className="text-center">
                                                        <input
                                                            type="checkbox"
                                                            className="checkbox-custom"
                                                            checked={isSelected}
                                                            onChange={() => toggleDevice(id)}
                                                        />
                                                    </td>
                                                    <td className="font-bold text-sm text-black">{carNum}</td>
                                                    <td>
                                                        <div className="text-sm font-medium text-black">{teamName || 'Unknown Team'}</div>
                                                    </td>
                                                    <td>
                                                        <div className="flex items-center gap-2">
                                                            {/* Progress bar only for Stage view or if data available */}
                                                            {device.onStage && (
                                                                <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                                                    <div
                                                                        className="bg-primary-blue h-full"
                                                                        style={{ width: '50%' }}
                                                                    ></div>
                                                                </div>
                                                            )}
                                                            <span className="text-gray-500 text-xs whitespace-nowrap font-medium">
                                                                {distance}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="flex items-center gap-2 text-black">
                                                            <span className="material-symbols-outlined text-lg text-primary-blue">speed</span>
                                                            <span className="text-sm font-bold">{(device.speed * 1.852).toFixed(1)} km/h</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {status === 'sent' && (
                                                            <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-1.5 rounded-lg w-fit whitespace-nowrap">
                                                                <span className="material-symbols-outlined text-sm">check_circle</span>
                                                                <span className="text-[10px] font-bold uppercase">Sent</span>
                                                            </div>
                                                        )}
                                                        {status === 'sending' && (
                                                            <div className="flex items-center gap-2 text-primary-blue bg-blue-50 px-3 py-1.5 rounded-lg w-fit whitespace-nowrap">
                                                                <span className="material-symbols-outlined text-sm animate-spin">sync</span>
                                                                <span className="text-[10px] font-bold uppercase tracking-wider">Sending...</span>
                                                            </div>
                                                        )}
                                                        {status === 'error' && (
                                                            <div className="flex items-center gap-2 text-red-600 bg-red-50 px-3 py-1.5 rounded-lg w-fit whitespace-nowrap">
                                                                <span className="material-symbols-outlined text-sm">warning</span>
                                                                <span className="text-[10px] font-bold uppercase">Error</span>
                                                            </div>
                                                        )}
                                                        {(!status) && (
                                                            <div className="flex items-center gap-2 text-gray-custom bg-gray-100 px-3 py-1.5 rounded-lg w-fit whitespace-nowrap">
                                                                <span className="material-symbols-outlined text-sm">hourglass_empty</span>
                                                                <span className="text-[10px] font-bold uppercase tracking-wider">Pending</span>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-8 py-8 border-t border-gray-200 bg-white/30">
                    <div className="flex gap-4">
                        <button onClick={onClose} className="btn-cancel flex-1 cursor-pointer">
                            CANCEL
                        </button>
                        {activeTab === 'redflag_cars' ? (
                            <button
                                onClick={handleRemoveRedFlag}
                                disabled={selectedDeviceIds.size === 0}
                                className="btn-clear-flag flex-[2] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: '1.25rem' }}>flag_circle</span>
                                <span className="truncate">REMOVE RED FLAG FROM SELECTED ({selectedDeviceIds.size})</span>
                            </button>
                        ) : (
                            <button
                                onClick={handleSendRedFlag}
                                disabled={selectedDeviceIds.size === 0}
                                className="btn-red-flag flex-[2] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: '1.25rem' }}>campaign</span>
                                <span className="truncate">SEND RED FLAG TO SELECTED ({selectedDeviceIds.size})</span>
                            </button>
                        )}
                    </div>
                    <div className="mt-4 flex items-center justify-center gap-2 text-xs text-emergency-red font-bold uppercase tracking-wider">
                        <span className="material-symbols-outlined text-sm">warning</span>
                        <span>Notification sent message to selected devices</span>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default RedFlagModal;
